import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Film, Users, BarChart3, Settings, Plus, Trash2, Edit, Eye,
  TrendingUp, Layers, Upload, X, Save, Search, Play, Tv,
  UserX, UserCheck, KeyRound, ChevronLeft, ChevronRight,
  Tag, Globe, Star, CreditCard, Check, Ticket, Calendar, Percent,
  Volume2, Joystick, ChevronUp, ChevronDown, RefreshCw, Sparkles, SlidersHorizontal, AlertTriangle, Scissors, GripVertical
} from 'lucide-react';
import { TrimEditor, fmtTrim } from './TrimEditor';
import { AiTrailerButton, AiTrailerBulkButton } from './AiTrailer';
import { ConfirmModal } from './TrashTab';
import FilterTimelineEditor from '../FilterTimelineEditor';
import { LanguageStats } from '../analytics/LanguageStats';
import {
  API, CATEGORIES_LIST, ALL_COUNTRIES, MEDIA_SPECS, LANGUAGES, TYPE_LABELS, TYPE_LABELS_PLURAL, uploadFileToS3, FilterBadge,
  formatFileSize, TimecodeInput, CountryPicker, FilterSelect, LoadingSpinner as SharedLoadingSpinner, validateMediaFile
} from './shared';
import { EpisodePreview, EpisodePreviewModal } from './EpisodePreview';
import { AiFilterAnalyze, useMediaUndo } from './core';

export function ContentForm({ content, onSave, onCancel, theme, forcedType }) {
  const light = theme === 'light';
  const [form, setForm] = useState(() => {
    const defaults = {
      title: '', description: '', type: forcedType || 'fiction', categories: ['nouveautes'], tags: [],
      year: 2026, duration: '', thumbnail_url: '', banner_url: '', logo_url: '', video_url: '',
      is_featured: false, rating: 'HD', status: 'draft', language: 'Français',
      age_rating: 'Tous publics', premium: false, country: 'France', trailer_url: '', hero_url: '', poster_url: '', mobile_banner_url: '',
      tv_banner_url: '', tv_screensaver_url: '',
      subtitles: [], audio_tracks: [],
      director: '', actors: ['', '', ''],
      geo_all_countries: true, geo_countries: [],
      intro_start: '', intro_end: '', outro_start: '', outro_end: '', intro_bumper: '',
      content_filters: [], filter_status: '', burned_subtitles_lang: '',
    };
    if (content) return { ...defaults, ...content };
    return defaults;
  });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState('image');
  // Compat: migrate old single category to array
  useEffect(() => {
    if (content?.category && !content?.categories) {
      set('categories', [content.category]);
    }
  }, [content]);
  const [tagInput, setTagInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [mobileBannerDims, setMobileBannerDims] = useState(null);
  useEffect(() => {
    if (!form.mobile_banner_url) { setMobileBannerDims(null); return; }
    const img = new Image();
    img.onload = () => setMobileBannerDims({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => setMobileBannerDims(null);
    img.src = form.mobile_banner_url;
  }, [form.mobile_banner_url]);
  // L'intro Originals nécessite la catégorie Originals Maya+
  useEffect(() => {
    const isOrig = (form.categories || [form.category]).includes('originals');
    if (!isOrig && form.intro_bumper === 'originals') set('intro_bumper', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.categories, form.intro_bumper]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  const [fileSizes, setFileSizes] = useState({});
  const [syncErrors, setSyncErrors] = useState([]);
  const [subDrag, setSubDrag] = useState(false);
  const [audioDrag, setAudioDrag] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const mediaUndo = useMediaUndo(setForm);

  // Auto-save new content as draft while typing (survives leaving the page without saving)
  const draftIdRef = useRef(null);
  const autoSaveTimer = useRef(null);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [trimTarget, setTrimTarget] = useState(null);
  useEffect(() => {
    if (content || !form.title.trim()) return;
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      const payload = { ...form, category: (form.categories || ['nouveautes'])[0], status: 'draft' };
      try {
        if (draftIdRef.current) {
          await axios.put(`${API}/contents/${draftIdRef.current}`, payload, { withCredentials: true });
        } else {
          const { data } = await axios.post(`${API}/contents`, payload, { withCredentials: true });
          draftIdRef.current = data?.id || null;
        }
        setDraftSavedAt(new Date());
      } catch { /* ignore */ }
    }, 1500);
    return () => clearTimeout(autoSaveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, content]);
  const inputCls = light ? 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400' : 'bg-white/5 border border-white/8 text-white';
  const hintCls = light ? 'text-gray-400' : 'text-white/25';

  const toggleCategory = (val) => {
    const cats = form.categories || [];
    if (cats.includes(val)) {
      if (cats.length > 1) set('categories', cats.filter(c => c !== val));
    } else {
      set('categories', [...cats, val]);
    }
  };

  const handleVideoUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
    const err = await validateMediaFile(file, field);
    if (err) {
      setUploadErrors(prev => ({ ...prev, [field]: err }));
      try { e.target.value = ''; } catch { /* drop path */ }
      return;
    }

    // Auto-detect duration for video files
    if (field === 'video_url' && file.type.startsWith('video/')) {
      const url = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        const secs = Math.round(video.duration);
        const h = Math.floor(secs / 3600);
        const m = Math.floor((secs % 3600) / 60);
        const formatted = h > 0 ? `${h}h ${m.toString().padStart(2, '0')}min` : `${m}min`;
        set('duration', formatted);
        URL.revokeObjectURL(url);
      };
      video.src = url;
    }

    setUploading(true);
    setUploadProgress(p => ({ ...p, [field]: 0 }));
    try {
      const fileUrl = await uploadFileToS3(file, (pct) => setUploadProgress(p => ({ ...p, [field]: pct })));
      mediaUndo.push(field, form[field]);
      set(field, fileUrl);
      setFileSizes(prev => ({ ...prev, [field]: file.size }));
      setUploadProgress(p => ({ ...p, [field]: 100 }));
      setTimeout(() => setUploadProgress(p => { const n = { ...p }; delete n[field]; return n; }), 1500);
    } catch (err) {
      console.error('Upload error:', err);
      alert('Upload échoué: ' + err.message);
      setUploadProgress(p => { const n = { ...p }; delete n[field]; return n; });
    } finally { setUploading(false); }
  };

  const handleTrackUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadProgress(p => ({ ...p, _track: 0 }));
    try {
      const { data: presigned } = await axios.post(`${API}/upload/presign`, {
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
      }, { withCredentials: true });
      await axios.put(presigned.upload_url, file, {
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        onUploadProgress: (evt) => {
          setUploadProgress(p => ({ ...p, _track: Math.round((evt.loaded * 100) / (evt.total || 1)) }));
        },
      });
      setUploadProgress(p => { const n = { ...p }; delete n._track; return n; });
      return { url: presigned.file_url, size: file.size, filename: file.name };
    } catch (err) {
      console.error('Track upload error:', err);
      alert('Upload échoué: ' + (err.response?.data?.detail || err.message));
      setUploadProgress(p => { const n = { ...p }; delete n._track; return n; });
      return null;
    } finally { setUploading(false); }
  };

  const addSubtitle = async (e) => {
    const result = await handleTrackUpload(e);
    if (result) {
      const ext = result.filename.split('.').pop().toLowerCase();
      set('subtitles', [...(form.subtitles || []), { url: result.url, language: 'Français', format: ext.toUpperCase(), size: result.size, filename: result.filename }]);
    }
  };

  const addAudioTrack = async (e) => {
    const result = await handleTrackUpload(e);
    if (result) {
      set('audio_tracks', [...(form.audio_tracks || []), { url: result.url, language: 'Français', size: result.size, filename: result.filename }]);
    }
  };

  const removeSubtitle = (idx) => set('subtitles', (form.subtitles || []).filter((_, i) => i !== idx));
  const removeAudioTrack = (idx) => set('audio_tracks', (form.audio_tracks || []).filter((_, i) => i !== idx));
  const updateSubLang = (idx, lang) => set('subtitles', (form.subtitles || []).map((s, i) => i === idx ? { ...s, language: lang } : s));
  const updateAudioLang = (idx, lang) => set('audio_tracks', (form.audio_tracks || []).map((a, i) => i === idx ? { ...a, language: lang } : a));

  // Sync check
  useEffect(() => {
    const errors = [];
    const subs = form.subtitles || [];
    const audios = form.audio_tracks || [];
    const subLangs = subs.map(s => s.language);
    const audioLangs = audios.map(a => a.language);
    const dupSubs = subLangs.filter((l, i) => subLangs.indexOf(l) !== i);
    const dupAudio = audioLangs.filter((l, i) => audioLangs.indexOf(l) !== i);
    if (dupSubs.length > 0) errors.push(`Sous-titres en doublon : ${[...new Set(dupSubs)].join(', ')}`);
    if (dupAudio.length > 0) errors.push(`Pistes audio en doublon : ${[...new Set(dupAudio)].join(', ')}`);
    if (form.video_url && audios.length > 0 && !audios.find(a => a.language === (form.language || 'Français'))) {
      errors.push(`Aucune piste audio ne correspond à la langue principale (${form.language || 'Français'})`);
    }
    if (audios.length > 0 && subs.length === 0) errors.push('Pistes audio multilingues sans sous-titres associés');
    setSyncErrors(errors);
  }, [form.subtitles, form.audio_tracks, form.video_url, form.language]);

  const MediaField = ({ field }) => {
    const spec = MEDIA_SPECS[field];
    const isVideo = spec.accept.includes('video');
    const size = fileSizes[field];
    const hasUrl = !!form[field];
    const [dragOver, setDragOver] = useState(false);
    const openPreview = () => {
      if (!hasUrl) return;
      setPreviewUrl(form[field]);
      setPreviewType(isVideo ? 'video' : 'image');
    };
    const onDrop = (e) => {
      e.preventDefault(); setDragOver(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) handleVideoUpload({ target: { files: [file] } }, field);
    };
    const clearField = () => { set(field, ''); setFileSizes(prev => { const n = { ...prev }; delete n[field]; return n; }); };
    return (
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`rounded-lg p-2 transition-all ${dragOver ? 'ring-2 ring-[#E9641A] bg-[#E9641A]/5' : ''}`}
      >
        <label className={`flex items-center gap-1.5 text-[10px] mb-0.5 uppercase tracking-wider ${light ? 'text-gray-500' : 'text-white/35'}`}>
          {spec.label}
          {!hasUrl && (
            <span className="px-1.5 py-0.5 rounded bg-[#E9641A] text-white text-[8px] font-bold normal-case tracking-normal" data-testid={`missing-media-${field}`}>Manquant</span>
          )}
        </label>
        <p className={`text-[9px] mb-0.5 ${hintCls}`}>{spec.dims} · {spec.formats} · Max {spec.maxSize}</p>
        {spec.desc && <p className={`text-[8px] mb-1 italic ${light ? 'text-gray-400' : 'text-white/25'}`}>{spec.desc}</p>}
        {dragOver ? (
          <div className={`flex items-center justify-center h-12 rounded-lg border-2 border-dashed border-[#E9641A] ${light ? 'bg-[#E9641A]/5' : 'bg-[#E9641A]/10'}`}>
            <p className="text-[#E9641A] text-xs font-semibold">Déposez le fichier ici</p>
          </div>
        ) : (
          <>
            <div className="flex gap-1.5">
              <input value={form[field] || ''} onChange={e => set(field, e.target.value)} placeholder="URL ou glisser-déposer..." className={`flex-1 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} />
              {hasUrl && <button type="button" onClick={openPreview} title="Aperçu" className={`px-2 py-2 rounded-lg text-xs transition-colors ${light ? 'bg-gray-100 text-gray-500 hover:text-[#E9641A]' : 'bg-white/8 text-white/40 hover:text-[#E9641A]'}`}><Eye size={12} /></button>}
              {hasUrl && <button type="button" onClick={clearField} title="Supprimer" className={`px-2 py-2 rounded-lg text-xs transition-colors ${light ? 'bg-gray-100 text-gray-300 hover:text-red-500 hover:bg-red-50' : 'bg-white/8 text-white/20 hover:text-red-400 hover:bg-red-400/10'}`}><Trash2 size={12} /></button>}
              <label title={hasUrl ? 'Remplacer' : 'Uploader'} className={`flex items-center px-2.5 py-2 rounded-lg text-xs cursor-pointer transition-colors ${light ? 'bg-gray-100 text-gray-400 hover:text-gray-700 hover:bg-gray-200' : 'bg-white/8 text-white/40 hover:text-white hover:bg-white/12'}`}>
                <Upload size={12} /><input type="file" accept={spec.accept} className="hidden" onChange={e => handleVideoUpload(e, field)} />
              </label>
            </div>
            {uploadErrors[field] && (
              <p className="text-[9px] mt-1 font-semibold text-red-500" data-testid={`upload-error-${field}`}>
                Upload annulé — {uploadErrors[field]}
              </p>
            )}
            {uploadProgress[field] !== undefined && (
              <div className="mt-1.5">
                <div className={`h-1.5 rounded-full overflow-hidden ${light ? 'bg-gray-100' : 'bg-white/10'}`}>
                  <div className={`h-full rounded-full transition-all duration-300 ${uploadProgress[field] === 100 ? 'bg-green-400' : 'bg-[#E9641A]'}`} style={{ width: `${uploadProgress[field]}%` }} />
                </div>
                <p className={`text-[9px] mt-0.5 ${uploadProgress[field] === 100 ? (light ? 'text-green-600' : 'text-green-400') : 'text-[#E9641A]'}`}>{uploadProgress[field] === 100 ? 'Upload terminé' : `Upload en cours... ${uploadProgress[field]}%`}</p>
              </div>
            )}
            {uploadProgress[field] === undefined && (size || form[field]) && <p className={`text-[9px] mt-0.5 ${light ? 'text-green-600' : 'text-green-400/60'}`}>{size ? formatFileSize(size) : (isVideo ? 'Fichier lié' : 'Image liée')}</p>}
            {field !== 'mobile_banner_url' && hasUrl && (
              <div className="mt-1.5" data-testid={`media-thumb-${field}`}>
                {isVideo ? (
                  <video src={form[field]} preload="metadata" muted playsInline onClick={openPreview} title="Cliquer pour agrandir"
                    className="h-[84px] rounded border cursor-pointer object-cover"
                    style={{ maxWidth: '150px', borderColor: light ? '#e5e7eb' : 'rgba(255,255,255,0.12)', background: light ? '#f3f4f6' : 'rgba(255,255,255,0.03)' }} />
                ) : (
                  <img src={form[field]} alt="" onClick={openPreview} title="Cliquer pour agrandir"
                    className="h-[84px] rounded border cursor-pointer object-contain"
                    style={{ maxWidth: '150px', borderColor: light ? '#e5e7eb' : 'rgba(255,255,255,0.12)', background: light ? '#f3f4f6' : 'rgba(255,255,255,0.03)' }}
                    onError={e => { e.target.style.display = 'none'; }} />
                )}
              </div>
            )}
            {field === 'mobile_banner_url' && hasUrl && mobileBannerDims && (() => {
              const ratio = mobileBannerDims.w / mobileBannerDims.h;
              const isPortrait23 = Math.abs(ratio - 2 / 3) < 0.02;
              return (
                <div className="flex items-start gap-2 mt-1.5" data-testid="mobile-banner-preview">
                  <img src={form[field]} alt="" className="w-14 h-[84px] object-contain rounded border flex-none" style={{ borderColor: isPortrait23 ? 'rgba(74,222,128,0.4)' : 'rgba(245,158,11,0.5)', background: light ? '#f3f4f6' : 'rgba(255,255,255,0.03)' }} />
                  <div>
                    <p className={`text-[9px] font-mono ${light ? 'text-gray-500' : 'text-white/50'}`} data-testid="mobile-banner-dims">{mobileBannerDims.w} x {mobileBannerDims.h} px</p>
                    {isPortrait23
                      ? <p className="text-[9px] text-green-500 font-semibold" data-testid="mobile-banner-ratio-ok">✓ Portrait 2:3 — image conservée telle quelle, sans redimensionnement</p>
                      : <p className="text-[9px] text-amber-500 font-semibold" data-testid="mobile-banner-ratio-warn">⚠ Ratio {ratio.toFixed(2)} ≠ 2:3 attendu (1080 x 1620). L'image n'est pas redimensionnée mais peut s'afficher recadrée sur mobile.</p>}
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>
    );
  };

  const doSave = async () => {
    if (saveBusy) return;
    setSaveBusy(true);
    clearTimeout(autoSaveTimer.current);
    try {
      await onSave({ ...form, id: content?.id || draftIdRef.current || undefined, category: (form.categories || ['nouveautes'])[0] });
      draftIdRef.current = null; // le brouillon a été consommé par la sauvegarde finale
      clearTimeout(autoSaveTimer.current);
    }
    finally { setSaveBusy(false); }
  };

  const actionBar = (suffix) => (
    <div className="flex items-center justify-end gap-2">
      {!content && draftSavedAt && (
        <span className={`text-[10px] mr-1 ${light ? 'text-gray-400' : 'text-white/30'}`} data-testid="draft-autosave-indicator">
          Brouillon enregistré à {draftSavedAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
        </span>
      )}
      <button onClick={onCancel} className={`px-4 py-1.5 text-xs border rounded-lg transition-colors ${light ? 'text-gray-500 hover:text-gray-800 border-gray-200' : 'text-white/45 hover:text-white border-white/10'}`}>Annuler</button>
      <button data-testid={`content-form-save-btn${suffix}`} disabled={saveBusy} onClick={doSave} className="px-4 py-1.5 text-xs bg-[#E9641A] hover:bg-[#FE975E] text-white font-semibold rounded-lg flex items-center gap-1.5 transition-colors disabled:opacity-50"><Save size={12} /> {saveBusy ? 'Enregistrement...' : 'Enregistrer'}</button>
    </div>
  );

  return (
    <div className={`rounded-xl border p-5 space-y-4 ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5'}`} data-testid="content-form">
      {mediaUndo.noticeEl}
      <div className="flex items-center justify-between">
        <h3 className={`text-sm font-bold ${light ? 'text-gray-800' : 'text-white'}`}>{content ? `Modifier : ${content.title}` : 'Nouveau contenu'}</h3>
        {actionBar('-top')}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="md:col-span-2"><label className={`block text-[10px] mb-1 uppercase tracking-wider ${light ? 'text-gray-500' : 'text-white/35'}`}>Titre</label><input value={form.title} onChange={e => set('title', e.target.value)} className={`w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E9641A] ${inputCls}`} /></div>
        <div><label className={`block text-[10px] mb-1 uppercase tracking-wider ${light ? 'text-gray-500' : 'text-white/35'}`}>Type</label><select value={form.type} onChange={e => set('type', e.target.value)} data-testid="content-form-type-select" className={`w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E9641A] ${inputCls}`}><option value="fiction">Fiction</option><option value="series">Série</option><option value="documentaire">Documentaire</option><option value="jeunesse">Jeunesse</option><option value="game">Jeu</option></select></div>
        <div><label className={`block text-[10px] mb-1 uppercase tracking-wider ${light ? 'text-gray-500' : 'text-white/35'}`}>Catégories</label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {CATEGORIES_LIST.map(c => {
              const selected = (form.categories || []).includes(c.value);
              return (
                <button type="button" key={c.value} onClick={() => toggleCategory(c.value)}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-medium transition-all border ${
                    selected
                      ? 'bg-[#E9641A]/15 border-[#E9641A]/40 text-[#E9641A]'
                      : light ? 'border-gray-200 text-gray-400 hover:border-gray-300' : 'border-white/10 text-white/30 hover:border-white/20'
                  }`}>{c.label}</button>
              );
            })}
          </div>
        </div>
      </div>
      <div><label className={`block text-[10px] mb-1 uppercase tracking-wider ${light ? 'text-gray-500' : 'text-white/35'}`}>Description</label><textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} className={`w-full rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E9641A] resize-none ${inputCls}`} /></div>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        <div><label className={`block text-[10px] mb-1 ${light ? 'text-gray-500' : 'text-white/35'}`}>Année</label><input type="number" value={form.year} onChange={e => set('year', parseInt(e.target.value) || 2026)} className={`w-full rounded-lg px-2 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} /></div>
        {form.type !== 'series' && <div><label className={`block text-[10px] mb-1 ${light ? 'text-gray-500' : 'text-white/35'}`}>Durée</label><input value={form.duration} onChange={e => set('duration', e.target.value)} placeholder="2h15" className={`w-full rounded-lg px-2 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} /></div>}
        <div><label className={`block text-[10px] mb-1 ${light ? 'text-gray-500' : 'text-white/35'}`}>Langue</label><select value={form.language || 'Français'} onChange={e => set('language', e.target.value)} className={`w-full rounded-lg px-2 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`}>{LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}</select></div>
        <div><label className={`block text-[10px] mb-1 ${light ? 'text-gray-500' : 'text-white/35'}`}>Pays</label><input value={form.country || ''} onChange={e => set('country', e.target.value)} className={`w-full rounded-lg px-2 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} /></div>
        <div><label className={`block text-[10px] mb-1 ${light ? 'text-gray-500' : 'text-white/35'}`}>Âge</label><select value={form.age_rating || 'Tous publics'} onChange={e => set('age_rating', e.target.value)} className={`w-full rounded-lg px-2 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`}><option value="Tous publics">Tous publics</option><option value="8+">8+</option><option value="12+">12+</option><option value="16+">16+</option><option value="18+">18+</option></select></div>
        <div><label className={`block text-[10px] mb-1 ${light ? 'text-gray-500' : 'text-white/35'}`}>Statut</label><select value={form.status || 'published'} onChange={e => set('status', e.target.value)} className={`w-full rounded-lg px-2 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`}><option value="published">Publié</option><option value="draft">Brouillon</option></select></div>
        <div><label className={`block text-[10px] mb-1 ${light ? 'text-gray-500' : 'text-white/35'}`}>Filtres de contenu</label><select value={form.filter_status || ''} onChange={e => set('filter_status', e.target.value)} data-testid="content-form-filter-status" className={`w-full rounded-lg px-2 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`}><option value="">Non concerné (pas de tag)</option><option value="todo">Filtres à appliquer</option><option value="applied">Filtres appliqués</option></select></div>
      </div>
      <div><label className={`block text-[10px] mb-1 uppercase tracking-wider ${light ? 'text-gray-500' : 'text-white/35'}`}>Tags</label>
        <div className="flex items-center gap-1.5 flex-wrap">
          {(form.tags || []).map(t => <span key={t} className="flex items-center gap-1 px-2 py-0.5 bg-[#E9641A]/20 text-[#E9641A] rounded text-[10px]">{t}<button onClick={() => set('tags', form.tags.filter(x => x !== t))}><X size={10} /></button></span>)}
          <input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (tagInput.trim()) { set('tags', [...(form.tags || []), tagInput.trim()]); setTagInput(''); } } }} placeholder="+ tag" className={`rounded px-2 py-0.5 text-[10px] w-20 focus:outline-none ${inputCls}`} />
        </div>
      </div>

      {/* ─── MEDIA IMAGES ─── */}
      <div>
        <h4 className={`text-xs font-semibold mb-2 ${light ? 'text-gray-700' : 'text-white/70'}`}>Images</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {form.type !== 'game' && <MediaField field="poster_url" />}
          <MediaField field="banner_url" />
          {form.type !== 'game' && <MediaField field="hero_url" />}
          {form.type !== 'game' && <MediaField field="logo_url" />}
          <MediaField field="thumbnail_url" />
          {form.type !== 'game' && <MediaField field="mobile_banner_url" />}
          {form.type !== 'game' && <MediaField field="tv_banner_url" />}
          {form.type !== 'game' && <MediaField field="tv_screensaver_url" />}
        </div>
        {form.type === 'game' && <p className={`text-[9px] mt-1 italic ${hintCls}`}>Pour les jeux, seules la vignette et la bannière sont utilisées. Le lien du jeu se gère dans l'onglet Jeux.</p>}
      </div>

      {/* ─── MEDIA VIDÉOS ─── */}
      {form.type !== 'game' && (
      <div>
        <h4 className={`text-xs font-semibold mb-2 ${light ? 'text-gray-700' : 'text-white/70'}`}>Vidéos</h4>
        <div className={`grid grid-cols-1 ${form.type !== 'series' ? 'md:grid-cols-2' : ''} gap-3`}>
          <MediaField field="trailer_url" />
          {form.type !== 'series' && <MediaField field="video_url" />}
        </div>
        {/* ─── DÉCOUPE VIDÉO ─── */}
        {(form.trailer_url || (form.type !== 'series' && form.video_url)) && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {form.trailer_url && (
              <button type="button" data-testid="trim-trailer-btn" onClick={() => setTrimTarget('trailer')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold border transition-colors ${light ? 'border-gray-200 text-gray-600 hover:border-[#E9641A] hover:text-[#E9641A]' : 'border-white/10 text-white/60 hover:border-[#E9641A] hover:text-[#E9641A]'}`}>
                <Scissors size={11} /> Découper la bande-annonce
              </button>
            )}
            {(form.trailer_trim_start > 0 || form.trailer_trim_end > 0) && <span className="text-[9px] text-[#E9641A] font-semibold" data-testid="trailer-trim-indicator">BA découpée {fmtTrim(form.trailer_trim_start, form.trailer_trim_end)}</span>}
            {form.type !== 'series' && form.video_url && (
              <button type="button" data-testid="trim-video-btn" onClick={() => setTrimTarget('video')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold border transition-colors ${light ? 'border-gray-200 text-gray-600 hover:border-[#E9641A] hover:text-[#E9641A]' : 'border-white/10 text-white/60 hover:border-[#E9641A] hover:text-[#E9641A]'}`}>
                <Scissors size={11} /> Découper la vidéo principale
              </button>
            )}
            {(form.video_trim_start > 0 || form.video_trim_end > 0) && <span className="text-[9px] text-[#E9641A] font-semibold" data-testid="video-trim-indicator">Vidéo découpée {fmtTrim(form.video_trim_start, form.video_trim_end)}</span>}
          </div>
        )}
        {/* ─── INTRO LOGO MAYA+ ─── */}
        {(() => {
          const isOriginals = (form.categories || [form.category]).includes('originals');
          return (
            <div className={`mt-3 p-3 rounded-lg border ${light ? 'bg-gray-50 border-gray-200' : 'bg-white/3 border-white/5'}`} data-testid="intro-bumper-section">
              <p className={`text-[10px] uppercase tracking-wider mb-2 ${light ? 'text-gray-500' : 'text-white/35'}`}>Intro logo Maya+</p>
              <div className="flex flex-wrap items-center gap-5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" data-testid="intro-bumper-normal-checkbox" className="accent-[#E9641A] w-3.5 h-3.5"
                    checked={form.intro_bumper === 'normal'}
                    onChange={e => set('intro_bumper', e.target.checked ? 'normal' : '')} />
                  <span className={`text-[11px] ${light ? 'text-gray-700' : 'text-white/70'}`}>Intro normale</span>
                </label>
                <label className={`flex items-center gap-2 ${isOriginals ? 'cursor-pointer' : 'opacity-35 cursor-not-allowed'}`}
                  title={isOriginals ? '' : 'Cochez la catégorie « Originals Maya+ » pour activer cette intro'}>
                  <input type="checkbox" data-testid="intro-bumper-originals-checkbox" className="accent-[#E9641A] w-3.5 h-3.5"
                    disabled={!isOriginals}
                    checked={form.intro_bumper === 'originals'}
                    onChange={e => set('intro_bumper', e.target.checked ? 'originals' : '')} />
                  <span className={`text-[11px] ${light ? 'text-gray-700' : 'text-white/70'}`}>Intro Maya+ Originals</span>
                </label>
              </div>
              <p className={`text-[9px] mt-1.5 italic ${hintCls}`}>Animation du logo jouée juste avant la vidéo ({form.type === 'series' ? 'avant chaque épisode' : 'au lancement du film'}) et au retour tout en arrière. N'affecte aucun timecode (filtres, découpes, reprise de lecture).</p>
            </div>
          );
        })()}
        {content?.id && (
          <AiTrailerButton
            contentId={content.id}
            hasTrailer={!!form.trailer_url}
            aiGenerated={!!form.trailer_generated_ai}
            trailerUrl={form.trailer_url}
            light={light}
            onDone={(url) => setForm(f => ({ ...f, trailer_url: url, trailer_generated_ai: true, trailer_trim_start: 0, trailer_trim_end: null }))}
            onDeleted={() => setForm(f => ({ ...f, trailer_url: '', trailer_generated_ai: false }))}
          />
        )}
        {trimTarget && (
          <TrimEditor
            url={trimTarget === 'trailer' ? form.trailer_url : form.video_url}
            label={trimTarget === 'trailer' ? 'Bande-annonce' : 'Vidéo principale'}
            start={form[trimTarget === 'trailer' ? 'trailer_trim_start' : 'video_trim_start'] || 0}
            end={form[trimTarget === 'trailer' ? 'trailer_trim_end' : 'video_trim_end'] || null}
            light={light}
            onClose={() => setTrimTarget(null)}
            onApply={async (ts, te) => {
              const ks = trimTarget === 'trailer' ? 'trailer_trim_start' : 'video_trim_start';
              const ke = trimTarget === 'trailer' ? 'trailer_trim_end' : 'video_trim_end';
              setForm(f => ({ ...f, [ks]: ts, [ke]: te }));
              if (content?.id) { try { await axios.put(`${API}/contents/${content.id}`, { [ks]: ts, [ke]: te }, { withCredentials: true }); } catch { /* saved with form */ } }
              setTrimTarget(null);
            }}
          />
        )}
        {form.type === 'series' && <p className={`text-[9px] mt-1 italic ${hintCls}`}>Les vidéos des épisodes se gèrent dans l'onglet Séries → Saisons & Épisodes</p>}
        {/* HLS Transcode section — only for existing content with video */}
        {content?.id && form.video_url && (
          <div className={`mt-3 p-3 rounded-lg border ${light ? 'bg-blue-50 border-blue-200' : 'bg-blue-500/5 border-blue-500/20'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs font-semibold ${light ? 'text-blue-700' : 'text-blue-300'}`}>Streaming adaptatif HLS</p>
                <p className={`text-[9px] ${light ? 'text-blue-500' : 'text-blue-400/60'}`}>Convertit en 360p + 720p + 1080p via AWS MediaConvert</p>
                {form.hls_url && <p className={`text-[9px] mt-0.5 ${light ? 'text-green-600' : 'text-green-400/60'}`}>HLS : {form.hls_url}</p>}
                {form.transcode_status && <p className={`text-[9px] mt-0.5 font-mono ${form.transcode_status === 'COMPLETE' ? 'text-green-400' : form.transcode_status === 'ERROR' ? 'text-red-400' : 'text-yellow-400'}`}>Statut : {form.transcode_status}</p>}
              </div>
              <button type="button" onClick={async () => {
                try {
                  const { data } = await axios.post(`${API}/admin/transcode/${content.id}`, {}, { withCredentials: true });
                  set('hls_url', data.hls_url);
                  set('transcode_status', data.status);
                  set('transcode_job_id', data.job_id);
                  alert(`Job lancé ! ID: ${data.job_id}`);
                } catch (err) { alert(err.response?.data?.detail || 'Erreur transcodage'); }
              }} data-testid="transcode-btn" className="px-3 py-1.5 text-[10px] font-semibold bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors">
                {form.transcode_status === 'SUBMITTED' ? 'En cours...' : 'Transcoder HLS'}
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* ─── SOUS-TITRES & PISTES AUDIO ─── */}
      {form.type !== 'game' && (<>
      <div
        onDragOver={e => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setSubDrag(true); } }}
        onDragLeave={() => setSubDrag(false)}
        onDrop={e => { e.preventDefault(); setSubDrag(false); const f = e.dataTransfer?.files?.[0]; if (f) addSubtitle({ target: { files: [f] } }); }}
        className={`rounded-lg p-2 transition-all ${subDrag ? 'ring-2 ring-[#E9641A] bg-[#E9641A]/5' : ''}`}
      >
        <div className="flex items-center justify-between mb-2">
          <h4 className={`text-xs font-semibold ${light ? 'text-gray-700' : 'text-white/70'}`}>{form.type === 'series' ? 'Sous-titres (bande-annonce)' : 'Sous-titres'}</h4>
          <label className="flex items-center gap-1.5 px-3 py-1.5 bg-[#E9641A]/10 text-[#E9641A] text-[10px] font-semibold rounded-lg cursor-pointer hover:bg-[#E9641A]/20 transition-colors">
            <Plus size={10} /> Ajouter SRT / VTT
            <input type="file" accept=".srt,.vtt,.ass,.ssa,.sub" className="hidden" onChange={addSubtitle} />
          </label>
        </div>
        <p className={`text-[9px] mb-2 ${hintCls}`}>Formats : SRT, VTT, ASS, SSA · Encodage UTF-8 · Glisser-déposer supporté{form.type === 'series' ? ' · Concerne uniquement la bande-annonce. Les sous-titres des épisodes se gèrent par épisode.' : ''}</p>
        {(form.subtitles || []).length === 0 && <p className={`text-[10px] italic ${light ? 'text-gray-300' : 'text-white/20'}`}>Aucun sous-titre ajouté</p>}
        <div className="space-y-1">
          {(form.subtitles || []).map((sub, i) => (
            <div key={i} className={`flex items-center gap-2 p-2 rounded-lg border ${light ? 'bg-gray-50 border-gray-200' : 'bg-white/3 border-white/5'}`}>
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${light ? 'bg-blue-50 text-blue-600' : 'bg-blue-500/10 text-blue-400'}`}>{sub.format || 'SRT'}</span>
              <select value={sub.language} onChange={e => updateSubLang(i, e.target.value)} className={`rounded px-2 py-1 text-[10px] ${inputCls}`}>
                {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
              <span className={`flex-1 text-[10px] truncate ${light ? 'text-gray-400' : 'text-white/30'}`}>{sub.filename}</span>
              {sub.size && <span className={`text-[9px] ${light ? 'text-gray-400' : 'text-white/20'}`}>{formatFileSize(sub.size)}</span>}
              <button onClick={() => removeSubtitle(i)} className="text-red-400 hover:text-red-300"><X size={12} /></button>
            </div>
          ))}
        </div>
        <div className="mt-3">
          <label className={`block text-[10px] mb-1 ${light ? 'text-gray-500' : 'text-white/35'}`}>Sous-titres intégrés à la vidéo (incrustés dans l'image)</label>
          <select value={form.burned_subtitles_lang || ''} onChange={e => set('burned_subtitles_lang', e.target.value)} data-testid="burned-subs-select"
            className={`rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`}>
            <option value="">Aucun</option>
            {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <p className={`text-[9px] mt-1 ${hintCls}`}>Pour les films dont les sous-titres sont déjà incrustés dans le fichier vidéo (ex : SLAM, LE MIRACLE). Affiché côté utilisateur : "Sous-titres {(form.burned_subtitles_lang || 'français').toLowerCase()}".</p>
        </div>
      </div>

      {/* ─── PISTES AUDIO ─── */}
      <div
        onDragOver={e => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setAudioDrag(true); } }}
        onDragLeave={() => setAudioDrag(false)}
        onDrop={e => { e.preventDefault(); setAudioDrag(false); const f = e.dataTransfer?.files?.[0]; if (f) addAudioTrack({ target: { files: [f] } }); }}
        className={`rounded-lg p-2 transition-all ${audioDrag ? 'ring-2 ring-[#E9641A] bg-[#E9641A]/5' : ''}`}
      >
        <div className="flex items-center justify-between mb-2">
          <h4 className={`text-xs font-semibold ${light ? 'text-gray-700' : 'text-white/70'}`}>{form.type === 'series' ? 'Pistes audio (bande-annonce)' : 'Pistes audio multilingues'}</h4>
          <label className="flex items-center gap-1.5 px-3 py-1.5 bg-[#E9641A]/10 text-[#E9641A] text-[10px] font-semibold rounded-lg cursor-pointer hover:bg-[#E9641A]/20 transition-colors">
            <Plus size={10} /> Ajouter une piste audio
            <input type="file" accept="audio/*,.aac,.mp3,.ogg,.flac,.wav,.m4a" className="hidden" onChange={addAudioTrack} />
          </label>
        </div>
        <p className={`text-[9px] mb-2 ${hintCls}`}>Formats : AAC, MP3, OGG, FLAC, WAV, M4A · Glisser-déposer supporté · {form.type === 'series' ? 'Synchronisé avec la bande-annonce. Les pistes audio des épisodes se gèrent par épisode.' : 'Doit être synchronisé avec la vidéo principale'}</p>
        {(form.audio_tracks || []).length === 0 && <p className={`text-[10px] italic ${light ? 'text-gray-300' : 'text-white/20'}`}>Aucune piste audio supplémentaire</p>}
        <div className="space-y-1">
          {(form.audio_tracks || []).map((track, i) => (
            <div key={i} className={`flex items-center gap-2 p-2 rounded-lg border ${light ? 'bg-gray-50 border-gray-200' : 'bg-white/3 border-white/5'}`}>
              <Volume2 size={14} className={light ? 'text-gray-400' : 'text-white/30'} />
              <select value={track.language} onChange={e => updateAudioLang(i, e.target.value)} className={`rounded px-2 py-1 text-[10px] ${inputCls}`}>
                {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
              <span className={`flex-1 text-[10px] truncate ${light ? 'text-gray-400' : 'text-white/30'}`}>{track.filename}</span>
              {track.size && <span className={`text-[9px] ${light ? 'text-gray-400' : 'text-white/20'}`}>{formatFileSize(track.size)}</span>}
              <button onClick={() => removeAudioTrack(i)} className="text-red-400 hover:text-red-300"><X size={12} /></button>
            </div>
          ))}
        </div>
      </div>
      </>)}

      {/* ─── RÉALISATEUR & ACTEURS ─── */}
      <div>
        <h4 className={`text-xs font-semibold mb-2 ${light ? 'text-gray-700' : 'text-white/70'}`}>Réalisateur & Acteurs principaux</h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <div><label className={`block text-[10px] mb-1 ${hintCls}`}>Réalisateur</label><input value={form.director || ''} onChange={e => set('director', e.target.value)} placeholder="Nom du réalisateur" className={`w-full rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} /></div>
          {[0, 1, 2].map(i => (
            <div key={i}><label className={`block text-[10px] mb-1 ${hintCls}`}>Acteur {i + 1}</label><input value={(form.actors || [])[i] || ''} onChange={e => { const a = [...(form.actors || ['', '', ''])]; a[i] = e.target.value; set('actors', a); }} placeholder={`Acteur principal ${i + 1}`} className={`w-full rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} /></div>
          ))}
        </div>
      </div>

      {/* ─── GÉO-RESTRICTION ─── */}
      <div>
        <h4 className={`text-xs font-semibold mb-2 ${light ? 'text-gray-700' : 'text-white/70'}`}>Disponibilité géographique</h4>
        <div className="flex items-center gap-3 mb-2">
          <label className={`flex items-center gap-1.5 text-[10px] cursor-pointer ${light ? 'text-gray-600' : 'text-white/60'}`}>
            <input type="checkbox" checked={form.geo_all_countries !== false} onChange={e => set('geo_all_countries', e.target.checked)} className="accent-[#E9641A]" />
            Disponible dans tous les pays
          </label>
        </div>
        {!form.geo_all_countries && (
          <div>
            <label className={`block text-[10px] mb-1 ${hintCls}`}>Pays</label>
            <CountryPicker selected={form.geo_countries || []} onChange={v => set('geo_countries', v)} inputCls={inputCls} light={light} />
          </div>
        )}
      </div>

      {/* ─── OUTRO (films : moment où l'écran de recommandation se lance) ─── */}
      {form.type !== 'series' && form.type !== 'game' && (
        <div>
          <h4 className={`text-xs font-semibold mb-2 ${light ? 'text-gray-700' : 'text-white/70'}`}>Outro / Fin du film</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div>
              <label className={`block text-[10px] mb-1 ${hintCls}`}>Outro début</label>
              <div className={`w-full rounded-lg px-3 py-2 text-xs ${inputCls}`} data-testid="movie-outro-start-input">
                <TimecodeInput light={light} value={form.outro_start} onChange={v => set('outro_start', v)} />
              </div>
            </div>
          </div>
          <p className={`text-[9px] mt-1.5 italic ${hintCls}`}>Moment (HH:MM:SS) où la proposition d'un contenu similaire s'affiche à la fin du film. Si vide : automatiquement les 10 dernières secondes.</p>
        </div>
      )}

      {/* ─── FILTRES PAR SECTION ─── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className={`text-xs font-semibold ${light ? 'text-gray-700' : 'text-white/70'}`}>Filtres par section</h4>
          <div className="flex items-center gap-2">
            <AiFilterAnalyze contentId={form.id} videoUrl={form.video_url} light={light} onDone={filters => set('content_filters', filters)} />
            <button type="button" onClick={() => set('content_filters', [...(form.content_filters || []), { start: '00:00:00', end: '00:00:00', filter_id: '' }])} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#E9641A]/10 text-[#E9641A] text-[10px] font-semibold rounded-lg hover:bg-[#E9641A]/20 transition-colors"><Plus size={10} /> Ajouter un filtre</button>
          </div>
        </div>
        <p className={`text-[9px] mb-2 ${hintCls}`}>Définissez les scènes à filtrer — Format : HH:MM:SS · Les types de filtres se gèrent dans l'onglet "Filtres" du menu</p>
        {(form.content_filters || []).length === 0 && <p className={`text-[10px] italic ${light ? 'text-gray-300' : 'text-white/20'}`}>Aucun filtre défini</p>}
        <div className="space-y-1">
          {(form.content_filters || []).map((f, i) => (
            <div key={i} className={`flex items-center gap-2 p-2 rounded-lg border ${light ? 'bg-gray-50 border-gray-200' : 'bg-white/3 border-white/5'}`}>
              <span className={`text-[9px] ${light ? 'text-gray-400' : 'text-white/30'}`}>Début</span>
              <div className={`rounded px-2 py-1.5 text-[10px] ${inputCls}`}><TimecodeInput light={light} value={f.start} onChange={v => { const a = [...(form.content_filters || [])]; a[i] = { ...a[i], start: v }; set('content_filters', a); }} /></div>
              <span className={`text-[9px] ${light ? 'text-gray-400' : 'text-white/30'}`}>Fin</span>
              <div className={`rounded px-2 py-1.5 text-[10px] ${inputCls}`}><TimecodeInput light={light} value={f.end} onChange={v => { const a = [...(form.content_filters || [])]; a[i] = { ...a[i], end: v }; set('content_filters', a); }} /></div>
              <FilterSelect value={f.filter_id || f.type || ''} onChange={val => { const a = [...(form.content_filters || [])]; a[i] = { ...a[i], filter_id: val }; set('content_filters', a); }} inputCls={inputCls} />
              {f.source === 'ai' && <span className={`flex items-center gap-0.5 text-[8px] font-bold px-1.5 py-0.5 rounded ${light ? 'text-purple-600 bg-purple-50' : 'text-purple-300 bg-purple-500/10'}`} title="Détecté par l'analyse IA — modifiable"><Sparkles size={8} /> IA</span>}
              <button type="button" onClick={() => set('content_filters', (form.content_filters || []).filter((_, j) => j !== i))} className="text-red-400 hover:text-red-300"><X size={14} /></button>
            </div>
          ))}
        </div>
      </div>

      {/* ─── SYNC ERRORS ─── */}
      {syncErrors.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3" data-testid="sync-errors">
          <p className="text-[10px] font-semibold text-yellow-400 mb-1">Alertes de synchronisation :</p>
          {syncErrors.map((err, i) => <p key={i} className="text-[10px] text-yellow-300/70">· {err}</p>)}
        </div>
      )}

      {/* ─── PREVIEW MODAL ─── */}
      {previewUrl && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center" onClick={() => setPreviewUrl(null)}>
          <div className="relative max-w-3xl max-h-[80vh] w-full mx-4" onClick={e => e.stopPropagation()}>
            <button onClick={() => setPreviewUrl(null)} className="absolute -top-8 right-0 text-white/60 hover:text-white"><X size={20} /></button>
            {previewType === 'video' ? (
              <video src={previewUrl} controls autoPlay className="w-full rounded-lg max-h-[75vh]" />
            ) : (
              <img src={previewUrl} alt="Aperçu" className="w-full rounded-lg max-h-[75vh] object-contain" />
            )}
          </div>
        </div>
      )}

      {uploading && Object.keys(uploadProgress).length > 0 && uploadProgress._track !== undefined && (
        <div className="mt-1">
          <div className={`h-1.5 rounded-full overflow-hidden ${light ? 'bg-gray-100' : 'bg-white/10'}`}>
            <div className="h-full rounded-full bg-[#E9641A] transition-all duration-300" style={{ width: `${uploadProgress._track || 0}%` }} />
          </div>
          <p className="text-[9px] mt-0.5 text-[#E9641A]">Upload en cours... {uploadProgress._track || 0}%</p>
        </div>
      )}
      {uploading && Object.keys(uploadProgress).length === 0 && <p className="text-xs text-[#E9641A]">Upload en cours...</p>}
      <div className="pt-1">{actionBar('')}</div>
    </div>
  );
}

